// Thin fetch wrapper. All requests are same-origin (via Vite proxy in dev,
// nginx in prod) so the HttpOnly auth cookie travels automatically.

export type User = {
  id: number;
  login: string;
  role: 'admin' | 'seller';
  display_name?: string | null;
};

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if (!res.ok) {
    const msg =
      data && typeof data === 'object' && data && 'error' in data
        ? String((data as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, data);
  }
  return data as T;
}

export const api = {
  get:    <T>(p: string)                 => request<T>('GET', p),
  post:   <T>(p: string, body?: unknown) => request<T>('POST', p, body),
  put:    <T>(p: string, body?: unknown) => request<T>('PUT', p, body),
  del:    <T>(p: string)                 => request<T>('DELETE', p),
};

export const auth = {
  login: (login: string, password: string) =>
    api.post<{ user: User }>('/api/auth/login', { login, password }),
  logout: () => api.post<{ ok: true }>('/api/auth/logout'),
  me:     () => api.get<{ user: User }>('/api/auth/me'),
};

// ============================================================
// Sellers
// ============================================================

export type Seller = {
  id: number;
  login: string;
  role: 'seller';
  display_name: string | null;
  contact_email: string | null;
  flowwow_shop_id: number | null;
  has_password: boolean;
  has_flowwow_token: boolean;
  flowwow_token_mask: string | null;
  is_active: 0 | 1;
  created_at: string;
  updated_at: string;
};

export type SellerListItem = Omit<
  Seller,
  'has_password' | 'has_flowwow_token' | 'flowwow_token_mask'
> & { has_token: boolean };

export type SellerCreate = {
  login: string;
  password: string;
  display_name?: string | null;
  contact_email?: string | null;
  flowwow_token?: string | null;
  flowwow_shop_id?: number | null;
};

export type SellerUpdate = Partial<{
  display_name: string | null;
  contact_email: string | null;
  flowwow_token: string | null;
  flowwow_shop_id: number | null;
  is_active: boolean;
}>;

export const sellersApi = {
  list:        ()              => api.get<{ sellers: SellerListItem[] }>('/api/sellers'),
  create:      (b: SellerCreate) => api.post<{ seller: Seller }>('/api/sellers', b),
  get:         (id: number)    => api.get<{ seller: Seller }>(`/api/sellers/${id}`),
  update:      (id: number, b: SellerUpdate) =>
    api.put<{ seller: Seller }>(`/api/sellers/${id}`, b),
  password:    (id: number, password: string) =>
    api.post<{ ok: true }>(`/api/sellers/${id}/password`, { password }),
  deactivate:  (id: number) => api.post<{ ok: true }>(`/api/sellers/${id}/deactivate`),
  activate:    (id: number) => api.post<{ ok: true }>(`/api/sellers/${id}/activate`),
  shops:       (id: number) => api.get<{ ok: true; raw: unknown }>(`/api/sellers/${id}/shops`),
};

// ============================================================
// Categories cache proxy
// ============================================================

// ============================================================
// Schedule groups
// ============================================================

export type Schedule = {
  id: number;
  seller_id: number;
  name: string;
  ship_days: number[];        // ISO weekday 1..7
  ship_time_msk: string;      // HH:MM
  deadline_anchor: 'absolute' | 'relative';
  deadline_days: number[];
  deadline_time_msk: string;
  deadline_offset_h: number;
  is_default: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type ScheduleInput = {
  seller_id?: number;
  name: string;
  ship_days: number[];
  ship_time_msk: string;
  deadline_anchor: 'absolute' | 'relative';
  deadline_days?: number[];
  deadline_time_msk?: string;
  deadline_offset_h?: number;
  is_default?: boolean;
  notes?: string | null;
};

export type SchedulePreview = {
  from: string;
  next_ship: string | null;
  windows: Array<{ ship: string | null; deadline: string | null }>;
};

export const schedulesApi = {
  list:    (sellerId?: number) =>
    api.get<{ schedules: Schedule[] }>(
      `/api/schedules${sellerId ? `?seller_id=${sellerId}` : ''}`,
    ),
  create:  (b: ScheduleInput & { seller_id: number }) =>
    api.post<{ schedule: Schedule }>('/api/schedules', b),
  get:     (id: number) =>
    api.get<{ schedule: Schedule }>(`/api/schedules/${id}`),
  update:  (id: number, b: Partial<ScheduleInput>) =>
    api.put<{ schedule: Schedule }>(`/api/schedules/${id}`, b),
  remove:  (id: number) =>
    api.del<{ ok: true }>(`/api/schedules/${id}`),
  preview: (id: number, count = 5) =>
    api.get<SchedulePreview>(`/api/schedules/${id}/preview?count=${count}`),
};

// ============================================================
// Products
// ============================================================

export type ConsistItem = { name: string; qty: number; unit: string };

export type Product = {
  id: number;
  seller_id: number;
  schedule_group_id: number | null;
  external_offer_id: string;
  flowwow_product_id: number | null;
  name: string;
  description: string | null;
  price_rub: number;
  category_id: number | null;
  sub_category_id: number | null;
  properties: Record<string, unknown>;
  consist: ConsistItem[];
  images: string[];
  is_active: boolean;
  current_window_state: 'pending' | 'open' | 'closed' | 'disabled';
  next_ship_dt: string | null;
  next_deadline_dt: string | null;
  last_production_time_hours: number | null;
  last_pushed_at: string | null;
  last_push_status: string | null;
  created_at: string;
  updated_at: string;
};

export type ProductCreate = {
  seller_id: number;
  schedule_group_id?: number | null;
  external_offer_id: string;
  name: string;
  description?: string | null;
  price_rub: number;
  category_id?: number | null;
  sub_category_id?: number | null;
  properties?: Record<string, unknown>;
  consist?: ConsistItem[];
  is_active?: boolean;
};

export type ProductUpdate = Partial<Omit<ProductCreate, 'seller_id'>>;

export type ProductsListParams = {
  seller_id?: number;
  schedule_group_id?: number;
  q?: string;
  is_active?: boolean;
  limit?: number;
  offset?: number;
};

export const productsApi = {
  list: (params: ProductsListParams = {}) => {
    const q = new URLSearchParams();
    if (params.seller_id != null)         q.set('seller_id',          String(params.seller_id));
    if (params.schedule_group_id != null) q.set('schedule_group_id',  String(params.schedule_group_id));
    if (params.q)                          q.set('q', params.q);
    if (params.is_active != null)         q.set('is_active',          params.is_active ? '1' : '0');
    if (params.limit != null)             q.set('limit',  String(params.limit));
    if (params.offset != null)            q.set('offset', String(params.offset));
    const qs = q.toString();
    return api.get<{ products: Product[]; total: number; limit: number; offset: number }>(
      `/api/products${qs ? `?${qs}` : ''}`,
    );
  },
  create:  (b: ProductCreate) => api.post<{ product: Product }>('/api/products', b),
  get:     (id: number) => api.get<{ product: Product }>(`/api/products/${id}`),
  update:  (id: number, b: ProductUpdate) =>
    api.put<{ product: Product }>(`/api/products/${id}`, b),
  remove:  (id: number) => api.del<{ ok: true }>(`/api/products/${id}`),

  uploadImages: async (id: number, files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    const res = await fetch(`/api/products/${id}/images`, {
      method: 'POST',
      credentials: 'same-origin',
      body: fd,
    });
    if (!res.ok) {
      let body: unknown = null;
      try { body = await res.json(); } catch { /* ignore */ }
      throw new ApiError(res.status, `upload failed (${res.status})`, body);
    }
    return (await res.json()) as { images: string[]; added: string[] };
  },
  deleteImage:  (id: number, index: number) =>
    api.del<{ images: string[] }>(`/api/products/${id}/images/${index}`),
  reorderImages: (id: number, order: number[]) =>
    api.post<{ images: string[] }>(`/api/products/${id}/images/reorder`, { order }),
};

// ============================================================
// Price import configs + imports
// ============================================================

export const KNOWN_PRICE_FIELDS = [
  'external_offer_id', 'name', 'description', 'price_rub', 'qty',
  'category_id', 'sub_category_id',
  'height', 'width',
  'country', 'manufacturer',
  'stem_length', 'flower_type', 'composition',
  'photo_url',
  'consist_name', 'consist_qty', 'consist_unit',
] as const;
export type KnownPriceField = typeof KNOWN_PRICE_FIELDS[number];

export type PriceConfig = {
  id: number;
  seller_id: number;
  name: string;
  is_default: boolean;
  sheet_index: number;
  header_row: number;
  data_start_row: number;
  column_map: Record<string, string>;  // field -> "A" or header text
  package_size_parser: 'split_qty_unit' | 'plain_number' | null;
  photo_source: 'none' | 'embedded' | 'url_column';
  photo_column: string | null;
  defaults: Record<string, string | number>;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type PriceConfigInput = Omit<PriceConfig, 'id' | 'created_at' | 'updated_at' | 'seller_id'> & {
  seller_id?: number;
};

export const priceConfigsApi = {
  list: (sellerId?: number) =>
    api.get<{ configs: PriceConfig[] }>(
      `/api/price-configs${sellerId ? `?seller_id=${sellerId}` : ''}`,
    ),
  create: (b: PriceConfigInput & { seller_id: number }) =>
    api.post<{ config: PriceConfig }>('/api/price-configs', b),
  get:    (id: number) => api.get<{ config: PriceConfig }>(`/api/price-configs/${id}`),
  update: (id: number, b: Partial<PriceConfigInput>) =>
    api.put<{ config: PriceConfig }>(`/api/price-configs/${id}`, b),
  remove: (id: number) => api.del<{ ok: true }>(`/api/price-configs/${id}`),
};

export type PreviewRow = {
  rowNum: number;
  raw: Record<string, string | number | null>;
  mapped: Record<string, unknown>;
  errors: string[];
};

export type PreviewResponse = {
  sheetName: string;
  headerRow: number;
  headers: Record<string, string>;
  colMap: Record<string, number>;
  total_rows: number;
  duplicates: Array<{
    sku: string;
    count: number;
    rows: Array<{ row: number; name: string | null }>;
  }>;
  preview: PreviewRow[];
};

export type CommitResponse = {
  import_id: number;
  inserted: number;
  updated: number;
  skipped: number;
  total_rows: number;
  errors: { row: number; error: string }[];
  stored_url: string;
};

export type PriceImport = {
  id: number;
  seller_id: number;
  config_id: number | null;
  uploaded_by_user_id: number | null;
  original_filename: string | null;
  stored_path: string | null;
  status: 'parsing' | 'success' | 'partial' | 'error' | 'rolled_back';
  rows_total: number;
  rows_inserted: number;
  rows_updated: number;
  rows_skipped: number;
  photos_added: number;
  errors: { row: number; error: string }[];
  duration_ms: number | null;
  uploaded_at: string;
};

async function multipart<T>(path: string, fd: FormData): Promise<T> {
  const res = await fetch(path, { method: 'POST', credentials: 'same-origin', body: fd });
  let body: unknown = null;
  const text = await res.text();
  if (text) { try { body = JSON.parse(text); } catch { body = text; } }
  if (!res.ok) {
    const msg = body && typeof body === 'object' && 'error' in body ? String((body as { error: unknown }).error) : `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, body);
  }
  return body as T;
}

export const priceImportsApi = {
  preview: (file: File, configId: number, limit = 20) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('config_id', String(configId));
    fd.append('limit', String(limit));
    return multipart<PreviewResponse>('/api/price-imports/preview', fd);
  },
  commit: (file: File, configId: number, overwriteExisting = true) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('config_id', String(configId));
    fd.append('overwrite_existing', overwriteExisting ? '1' : '0');
    return multipart<CommitResponse>('/api/price-imports/commit', fd);
  },
  list: (sellerId?: number) =>
    api.get<{ imports: PriceImport[] }>(
      `/api/price-imports${sellerId ? `?seller_id=${sellerId}` : ''}`,
    ),
  get: (id: number) =>
    api.get<{ import: PriceImport }>(`/api/price-imports/${id}`),
};

// ============================================================
// Publish
// ============================================================

export type PublishLogEntry = {
  id: number;
  product_id: number | null;
  seller_id: number | null;
  action: 'create' | 'update' | 'hide' | 'unhide' | 'archive' | 'unarchive' | 'stocks' | 'prices';
  response_status: number | null;
  duration_ms: number | null;
  pushed_at: string;
};

export type PublishLogDetail = PublishLogEntry & {
  request_body: unknown;
  response_body: unknown;
};

export type PublishOneResult =
  | { dry_run: true; action: 'create' | 'update'; payload: unknown; productionTimeHours: number | null }
  | { ok: true; action: 'create' | 'update'; productionTimeHours: number | null; response: unknown };

export const publishApi = {
  one: (productId: number, dryRun = false) =>
    api.post<PublishOneResult>(`/api/products/${productId}/publish${dryRun ? '?dry_run=1' : ''}`),
  bulk: (sellerId: number, ids: number[], dryRun = false) =>
    api.post<unknown>('/api/publish/bulk', { seller_id: sellerId, ids, dry_run: dryRun }),
  log: (params: { product_id?: number; seller_id?: number; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.product_id != null) q.set('product_id', String(params.product_id));
    if (params.seller_id != null)  q.set('seller_id',  String(params.seller_id));
    if (params.limit != null)      q.set('limit',      String(params.limit));
    const qs = q.toString();
    return api.get<{ entries: PublishLogEntry[] }>(`/api/publish/log${qs ? `?${qs}` : ''}`);
  },
  logEntry: (id: number) => api.get<{ entry: PublishLogDetail }>(`/api/publish/log/${id}`),
};

// ============================================================
// Worker (hourly)
// ============================================================

export type WorkerSummary = {
  started_at: string;
  finished_at: string;
  duration_ms: number;
  dry_run: boolean;
  total_products: number;
  open_to_closed: number;
  closed_to_open: number;
  stayed_open: number;
  stayed_closed: number;
  skipped_no_token: number;
  skipped_no_schedule: number;
  seller_buckets: number;
  batches: Array<{
    sellerId: number;
    action: 'update' | 'hide' | 'unhide';
    count: number;
    status: number;
    duration_ms: number;
    error?: unknown;
  }>;
};

export type WorkerStatus = {
  enabled: boolean;
  cron: string;
  running: boolean;
  last: WorkerSummary | null;
};

export const workerApi = {
  status:  () => api.get<WorkerStatus>('/api/worker/status'),
  runNow:  (dryRun = false) => api.post<WorkerSummary | { skipped: 'already_running' }>(
    `/api/worker/run-now${dryRun ? '?dry_run=1' : ''}`,
  ),
};

export const categoriesApi = {
  tree:     (force = false) =>
    api.get<{ from: 'cache' | 'live'; data: unknown; fetched_at?: string }>(
      `/api/categories/tree${force ? '?force=1' : ''}`,
    ),
  subProps: (categoryId: number, subCategoryId: number, force = false) =>
    api.get<{ from: 'cache' | 'live'; data: unknown; fetched_at?: string }>(
      `/api/categories/sub-props?categoryId=${categoryId}&subCategoryId=${subCategoryId}${force ? '&force=1' : ''}`,
    ),
};
